/**
 * A TELA DE ADMINISTRAÇÃO: a porta de quem hospeda o sistema.
 *
 * Ela é o front-end das quinze rotas de `/v1/hospedagem/*`, que nasceram nas etapas 1 e 2 e
 * ficaram sem tela. Enquanto ela não existiu, criar um Campo era comando no terminal, e o roteiro
 * de implantação parava no passo A6 mandando abrir uma tela que não existia.
 *
 * ## Três coisas que esta tela faz diferente do resto do painel, e por quê
 *
 * **Ela não usa a `BarraDeCima`.** Aquela barra mostra nome, cargo e alcance de uma conta de
 * painel, e esta conta não é de painel: não tem cargo, não tem congregação e não tem Campo. A
 * barra daqui diz outra coisa, que é o endereço da conta e o aviso de que ela alcança todos os
 * Campos.
 *
 * **O segundo fator é obrigatório ANTES de ver qualquer Campo.** Quem decide é o servidor, em
 * toda rota: enquanto o fator não estiver ligado, a sessão abre e não alcança nada. Esta tela não
 * é a trava, é a educação, e é a mesma escolha do painel para a conta nova.
 *
 * **A entrada é num pedido só**, com e-mail, senha e código juntos. O porquê está em
 * `api-da-administracao.ts`.
 *
 * ## O que ela NÃO faz, e é decisão escrita
 *
 * **Ela não cria conta de Administração.** Essa nasce por comando, na hospedagem, porque uma tela
 * que cria a conta mais poderosa do sistema a partir de dentro dela mesma é uma chave que faz
 * cópias de si. A tela também não desliga o segundo fator de ninguém, pelo mesmo motivo.
 */
import { useCallback, useEffect, useState, type FormEvent, type JSX } from 'react'
import { ErroDoServidor } from '../api'
import { administracao, type EuDaAdministracao } from '../api-da-administracao'
import { NOME_DO_SISTEMA } from '../identidade'
import { irPara } from '../rotas'
import { OsCampos } from './administracao/OsCampos'
import { CaixaDeSaida } from './administracao/CaixaDeSaida'

type Estado =
  | { fase: 'perguntando' }
  | { fase: 'fora' }
  | { fase: 'dentro'; eu: EuDaAdministracao }

export function Administracao({
  parte = 'campos'
}: {
  /** Qual tela da Administração: a lista dos Campos, ou a caixa de saída. */
  parte?: 'campos' | 'caixa-de-saida'
} = {}): JSX.Element {
  const [estado, setEstado] = useState<Estado>({ fase: 'perguntando' })

  const perguntar = useCallback(() => {
    administracao
      .eu()
      .then((quem) => setEstado(quem.dentro ? { fase: 'dentro', eu: quem } : { fase: 'fora' }))
      // Servidor fora do ar ou internet caída: a tela de entrar é o lugar certo de esperar.
      .catch(() => setEstado({ fase: 'fora' }))
  }, [])

  useEffect(perguntar, [perguntar])

  const sair = useCallback(() => {
    void administracao.sair().finally(() => setEstado({ fase: 'fora' }))
  }, [])

  if (estado.fase === 'perguntando') return <p className="tela-centro">Abrindo...</p>

  if (estado.fase === 'fora') {
    return <EntrarNaAdministracao aoEntrar={(eu) => setEstado({ fase: 'dentro', eu })} />
  }

  /*
   * A CONTA NÃO VÊ CAMPO NENHUM ANTES DE LIGAR O SEGUNDO FATOR.
   *
   * O servidor recusa de qualquer jeito, em toda rota de Campo. Esta linha evita mostrar uma
   * lista vazia com um erro em cima, que faria a pessoa achar que não existe Campo nenhum.
   */
  if (estado.eu.deve_ligar_segundo_fator) {
    return <LigarOSegundoFator eu={estado.eu} aoLigar={perguntar} aoSair={sair} />
  }

  if (parte === 'caixa-de-saida') return <CaixaDeSaida eu={estado.eu} aoSair={sair} />

  return <OsCampos eu={estado.eu} aoSair={sair} />
}

/** A barra de cima daqui: o nome do sistema, quem entrou, e sair. */
export function BarraDaAdministracao({
  eu,
  aoSair
}: {
  eu: EuDaAdministracao | null
  aoSair: () => void
}): JSX.Element {
  return (
    <header className="barra-do-painel da-administracao so-tela">
      <span className="marca">{NOME_DO_SISTEMA}</span>
      <span className="quem">
        Administração
        {eu !== null && (
          <>
            <br />
            <span className="cargo">{eu.email}</span>
            <br />
            todos os Campos
          </>
        )}
      </span>
      <button onClick={aoSair}>Sair</button>
    </header>
  )
}

/**
 * Entrar: e-mail, senha e código, num formulário só.
 *
 * **A recusa é sempre a mesma frase**, qualquer que seja o motivo, e ela vem do servidor inteira.
 * Dizer "o e-mail não existe" contaria a quem tentou que aquele endereço não administra este
 * servidor; dizer "o código está errado" contaria que a senha estava certa.
 */
function EntrarNaAdministracao({
  aoEntrar
}: {
  aoEntrar: (eu: EuDaAdministracao) => void
}): JSX.Element {
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [entrando, setEntrando] = useState(false)

  async function entrar(evento: FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setEntrando(true)

    try {
      const feita = await administracao.entrar(email.trim(), senha, codigo.trim())
      setSenha('')
      setCodigo('')
      aoEntrar({
        nome: feita.nome,
        email: feita.email,
        deve_ligar_segundo_fator: feita.deve_ligar_segundo_fator,
        codigos_de_emergencia_restantes: null
      })
    } catch (falha) {
      setErro(
        falha instanceof ErroDoServidor
          ? falha.message
          : 'Não foi possível entrar. Tente de novo.'
      )
      setSenha('')
      setCodigo('')
      setEntrando(false)
    }
  }

  return (
    <main className="entrar" data-passo="administracao">
      <h1>{NOME_DO_SISTEMA}</h1>
      <p className="subtitulo">Administração</p>

      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      <form onSubmit={(e) => void entrar(e)}>
        <label>
          E-mail
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
          />
        </label>

        <label>
          Senha
          <input
            type="password"
            name="senha"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        <label>
          Código do aplicativo
          <input
            type="text"
            name="codigo"
            className="codigo"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            spellCheck={false}
            maxLength={20}
          />
          <span className="ajuda-do-codigo">
            Os seis números do aplicativo autenticador. Um dos dez códigos de emergência também
            serve, e gasta um deles.
            <br />
            <strong>Na primeira entrada desta conta, deixe em branco:</strong> o segundo fator
            ainda não existe, e ligar ele é a primeira coisa que esta tela faz.
          </span>
        </label>

        <button className="principal" type="submit" disabled={entrando}>
          {entrando ? 'Entrando...' : 'Entrar'}
        </button>
      </form>

      <p className="rodape">
        Esta é a porta de quem hospeda o sistema, e não a do painel dos pastores. Nenhuma conta de
        pastor entra aqui, e esta conta não abre tela de painel nenhuma.
        <br />
        <strong>Ela não tem "esqueci minha senha".</strong> A volta é pelo comando, na hospedagem.
      </p>

      <p className="pe-da-tela">
        <button className="como-link" type="button" onClick={() => irPara('/')}>
          Ir para o painel dos pastores
        </button>
      </p>
    </main>
  )
}

/**
 * LIGAR O SEGUNDO FATOR, antes de qualquer Campo.
 *
 * Os dez códigos de emergência aparecem **uma vez**, e a tela diz isso com todas as letras. Eles
 * são a única volta no dia em que o celular se perder: não existe comando que desligue o segundo
 * fator desta conta, e isso é de propósito.
 */
function LigarOSegundoFator({
  eu,
  aoLigar,
  aoSair
}: {
  eu: EuDaAdministracao
  aoLigar: () => void
  aoSair: () => void
}): JSX.Element {
  const [comecado, setComecado] = useState<{ quadrado: string; segredo: string } | null>(null)
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)
  const [guardados, setGuardados] = useState<string[] | null>(null)

  useEffect(() => {
    administracao
      .comecarOFator()
      .then((c) => setComecado({ quadrado: c.quadrado, segredo: c.segredo }))
      .catch((falha: unknown) => {
        setErro(
          falha instanceof ErroDoServidor
            ? falha.message
            : 'Não foi possível começar a ligar o segundo fator.'
        )
      })
  }, [])

  async function confirmar(evento: FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setIndo(true)

    try {
      const ligado = await administracao.confirmarOFator(codigo.trim())
      setGuardados(ligado.codigos_de_emergencia)
    } catch (falha) {
      setErro(
        falha instanceof ErroDoServidor ? falha.message : 'Não foi possível confirmar o código.'
      )
    } finally {
      setIndo(false)
      setCodigo('')
    }
  }

  if (guardados !== null) {
    return (
      <>
        <BarraDaAdministracao eu={eu} aoSair={aoSair} />
        <main className="folha passo">
          <h1>Guarde estes dez códigos</h1>

          <p className="recado feito" role="status">
            O segundo fator está ligado. Agora esta conta alcança os Campos.
          </p>

          <p className="o-que-fazer">
            <strong>Escreva os dez num papel</strong>, e guarde o papel longe do celular. Eles
            aparecem uma vez só, e cada um serve uma vez.
          </p>

          <ol className="codigos-de-emergencia">
            {guardados.map((c) => <li key={c}>{c}</li>)}
          </ol>

          <p className="explicacao atencao">
            Perdidos a senha, o celular <strong>e</strong> o papel, não há volta para esta conta:
            não existe comando que desligue o segundo fator da Administração. A saída seria criar
            outra conta, com outro e-mail.
          </p>

          <div className="linha-form">
            <button className="principal" onClick={aoLigar}>Ver os Campos</button>
          </div>
        </main>
      </>
    )
  }

  return (
    <>
      <BarraDaAdministracao eu={eu} aoSair={aoSair} />
      <main className="folha passo">
        <h1>Ligue o segundo fator</h1>

        <p className="o-que-fazer">
          Esta conta cria Campos e gera códigos que entregam uma cópia inteira de um Campo a um
          computador novo. <strong>Enquanto o segundo fator não estiver ligado, ela não alcança
          Campo nenhum.</strong>
        </p>

        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {comecado === null && erro === null && <p className="recado aguarde">Preparando...</p>}

        {comecado !== null && (
          <>
            <p className="explicacao">
              Abra o aplicativo autenticador do seu celular e aponte a câmera para o quadrado.
            </p>

            <img className="quadrado" src={comecado.quadrado} alt="" />

            <p className="explicacao">
              Não consegue ler o quadrado? Digite este segredo no aplicativo, à mão:
            </p>
            <p className="segredo">{comecado.segredo}</p>

            <form onSubmit={(e) => void confirmar(e)}>
              <label>
                O número que o aplicativo está mostrando
                <input
                  className="campo codigo"
                  type="text"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  spellCheck={false}
                  maxLength={10}
                  required
                />
              </label>

              <button
                className="principal"
                type="submit"
                disabled={indo || codigo.trim().length < 6}
              >
                {indo ? 'Conferindo...' : 'Ligar o segundo fator'}
              </button>
            </form>
          </>
        )}
      </main>
    </>
  )
}
